"use client";

import { Loader2 } from "lucide-react";
import Image from "next/image";
import React from "react";
import type { Language } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";
import {
	buildPosterBackgroundUrl,
	getPosterContent,
	getPosterDimensions,
	getPosterLayout,
	getPosterPalette,
	type PosterVariant,
} from "./poster-layout";
import type { SponsoredHolding } from "./types";
import { buildTokenImageUrl, getSponsoredDisplayCopy } from "./utils";

function toPercent(value: number, total: number) {
	return `${(value / total) * 100}%`;
}

export function PosterLoadingPreview({
	holding,
	rank,
	variant,
	language,
	label,
	className,
}: {
	holding: SponsoredHolding;
	rank: number;
	variant: PosterVariant;
	language: Language;
	label: string;
	className?: string;
}) {
	const layout = getPosterLayout(variant);
	const dimensions = getPosterDimensions(variant);
	const palette = getPosterPalette(rank);
	const content = getPosterContent(holding, variant, language);
	const isMobile = variant === "mobile";
	const displayCopy = getSponsoredDisplayCopy(holding.token, language);

	return (
		<div
			className={cn("relative h-full w-full overflow-hidden", className)}
			style={{
				borderRadius: `${layout.radius}px`,
				backgroundColor: "#05070B",
			}}
		>
			<Image
				src={buildPosterBackgroundUrl(rank, variant)}
				alt=""
				fill
				priority={false}
				unoptimized
				sizes={variant === "mobile" ? "100vw" : "880px"}
				className="object-cover"
			/>
			<div
				className="absolute inset-0"
				style={{
					background:
						"radial-gradient(circle at 50% 16%, rgba(255,255,255,0.12), transparent 24%), radial-gradient(circle at 18% 78%, rgba(255,255,255,0.05), transparent 32%), radial-gradient(circle at 82% 20%, rgba(169,211,255,0.1), transparent 28%)",
				}}
			/>
			<div className="absolute inset-0" style={{ background: layout.overlay }} />
			<div
				className="absolute inset-0 animate-pulse"
				style={{
					background:
						"linear-gradient(110deg, transparent 24%, rgba(255,255,255,0.08) 38%, transparent 52%)",
				}}
			/>
			<div
				className="absolute left-1/2 -translate-x-1/2"
				style={{
					top: toPercent(layout.icon.top, dimensions.height),
					width: toPercent(layout.icon.containerSize, dimensions.width),
					height: toPercent(layout.icon.containerSize, dimensions.height),
				}}
			>
				<div
					className="relative flex h-full w-full items-center justify-center rounded-full"
					style={{
						background: "rgba(5,10,20,0.22)",
						border: `${layout.icon.borderWidth}px solid ${palette.medal.colors[0]}`,
						boxShadow: `0 16px 48px ${palette.placeholderGlow}`,
					}}
				>
					{holding.token.id ? (
						<Image
							src={buildTokenImageUrl(holding.token.id)}
							alt={content.tokenName}
							fill
							unoptimized
							sizes={isMobile ? "156px" : "188px"}
							className="rounded-full object-cover"
							style={{
								inset: `${((layout.icon.containerSize - layout.icon.size) / 2 / layout.icon.containerSize) * 100}%`,
							}}
						/>
					) : (
						<div
							className="flex items-center justify-center rounded-full text-white"
							style={{
								width: `${(layout.icon.size / layout.icon.containerSize) * 100}%`,
								height: `${(layout.icon.size / layout.icon.containerSize) * 100}%`,
								background: `linear-gradient(135deg, ${palette.medal.colors[0]} 0%, ${palette.accentSoft} 100%)`,
								fontSize: `${(layout.icon.size / layout.icon.containerSize) * (isMobile ? 58 : 72)}px`,
								fontWeight: 700,
							}}
						>
							{content.monogram}
						</div>
					)}
				</div>
			</div>
			<div
				className="absolute left-1/2 -translate-x-1/2 text-center"
				style={{
					top: toPercent(
						layout.icon.top + layout.icon.containerSize + (isMobile ? 18 : 22),
						dimensions.height,
					),
					width: `min(${toPercent(isMobile ? 640 : 1080, dimensions.width)}, calc(100% - ${isMobile ? 24 : 32}px))`,
				}}
			>
				<div
					className="mx-auto line-clamp-2 font-bold text-white/92"
					style={{
						fontSize: isMobile ? "19px" : "25px",
						lineHeight: 1.4,
						textShadow: "0 4px 18px rgba(0,0,0,0.38)",
					}}
				>
					{displayCopy}
				</div>
			</div>
			<div
				className="absolute bottom-0 left-0 right-0 flex justify-center"
				style={{
					paddingLeft: toPercent(layout.content.paddingX, dimensions.width),
					paddingRight: toPercent(layout.content.paddingX, dimensions.width),
					paddingBottom: toPercent(layout.content.paddingBottom, dimensions.height),
				}}
			>
				<div
					className="relative flex items-center justify-center"
					style={{
						width: `min(${toPercent(
							variant === "mobile" ? 320 : 360,
							dimensions.width,
						)}, 100%)`,
						padding: variant === "mobile" ? "12px 16px" : "14px 18px",
						gap: variant === "mobile" ? "8px" : "10px",
						borderRadius: `${variant === "mobile" ? 18 : 20}px`,
						background: "rgba(5,10,20,0.58)",
						border: `1px solid ${palette.panelBorder}`,
						boxShadow: `0 18px 42px ${palette.panelGlow}`,
						backdropFilter: "blur(10px)",
					}}
				>
					<Loader2
						className={cn(
							"animate-spin text-white/85",
							variant === "mobile" ? "h-3.5 w-3.5" : "h-4 w-4",
						)}
						style={{
							filter: `drop-shadow(0 0 8px ${palette.placeholderGlow})`,
						}}
					/>
					<div
						className="text-center font-semibold text-white/90"
						style={{
							fontSize: variant === "mobile" ? "12px" : "14px",
							letterSpacing: "0.01em",
							lineHeight: 1,
						}}
					>
						{label}
					</div>
				</div>
			</div>
		</div>
	);
}
